import type {CSSProperties, HTMLAttributes, ReactNode} from 'react';

import {cn} from '../../helpers/cn';

type SkeletonBlockProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
};

function SkeletonBlock({active = true, className, style, ...props}: SkeletonBlockProps) {
  return (
    <div
      {...props}
      aria-hidden
      className={cn('rounded-[4px] bg-gray-19', active && 'animate-pulse', className)}
      style={style}
    />
  );
}

export type SkeletonAvatarProps = {
  active?: boolean;
  size?: 'small' | 'default' | 'large' | number;
  shape?: 'circle' | 'square';
  style?: CSSProperties;
  className?: string;
};

function SkeletonAvatar({
  active = true,
  size = 'default',
  shape = 'circle',
  style,
  className
}: SkeletonAvatarProps) {
  const sizeMap = {small: 24, default: 40, large: 40} as const;
  const px = typeof size === 'number' ? size : sizeMap[size];

  return (
    <SkeletonBlock
      active={active}
      className={cn(shape === 'circle' ? 'rounded-full' : 'rounded-[4px]', className)}
      style={{width: px, height: px, ...style}}
    />
  );
}

export type SkeletonButtonProps = {
  active?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: CSSProperties;
  className?: string;
};

function SkeletonButton({active = true, size = 'default', style, className}: SkeletonButtonProps) {
  const heightMap = {small: 24, default: 32, large: 40} as const;

  return (
    <SkeletonBlock
      active={active}
      className={cn('rounded-[4px]', className)}
      style={{width: style?.width ?? 48, height: style?.height ?? heightMap[size], ...style}}
    />
  );
}

export type SkeletonInputProps = {
  active?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: CSSProperties;
  className?: string;
};

function SkeletonInput({active = true, size = 'default', style, className}: SkeletonInputProps) {
  const heightMap = {small: 24, default: 32, large: 40} as const;

  return (
    <SkeletonBlock
      active={active}
      className={cn('rounded-[4px]', className)}
      style={{width: style?.width ?? 160, height: style?.height ?? heightMap[size], ...style}}
    />
  );
}

export type SkeletonProps = {
  active?: boolean;
  loading?: boolean;
  title?: boolean | {width?: number | string; style?: CSSProperties};
  paragraph?: boolean | {rows?: number; width?: number | string | Array<number | string>};
  children?: ReactNode;
  className?: string;
};

export function Skeleton({
  active = true,
  loading = true,
  title = true,
  paragraph = true,
  children,
  className
}: SkeletonProps) {
  if (!loading) {
    return <>{children}</>;
  }

  const titleConfig = title === true ? {} : title === false ? null : title;
  const paragraphConfig = paragraph === true ? {rows: 3} : paragraph === false ? null : paragraph;
  const rows = paragraphConfig?.rows ?? 3;
  const widths = Array.isArray(paragraphConfig?.width)
    ? paragraphConfig.width
    : Array.from({length: rows}, (_, i) =>
        i === rows - 1 ? (paragraphConfig?.width ?? '60%') : (paragraphConfig?.width ?? '100%')
      );

  return (
    <div
      className={cn('flex w-full flex-col gap-3', className)}
      aria-busy="true"
      aria-live="polite"
    >
      {titleConfig && (
        <SkeletonBlock
          active={active}
          className="h-4"
          style={{width: titleConfig.width ?? '38%', ...titleConfig.style}}
        />
      )}
      {paragraphConfig && (
        <div className="flex flex-col gap-3">
          {Array.from({length: rows}, (_, i) => {
            const width = widths[i] ?? '100%';
            return (
              <SkeletonBlock
                key={i}
                active={active}
                className="h-4"
                style={{width: Array.isArray(width) ? width[0] : width}}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

Skeleton.Avatar = SkeletonAvatar;
Skeleton.Button = SkeletonButton;
Skeleton.Input = SkeletonInput;
